import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  COUNTRIES,
  createSubjectSchema,
  type RegistryLookups,
  type RoleDataField,
  type SubjectResponse,
  type TaxIdCheckResponse,
} from '@k-management/shared';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { Field } from '../../components/Field.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { apiRequest } from '../../lib/api.js';
import { type FieldErrors, showServerError, validate } from '../../lib/form-validation.js';
import { RoleFields } from './RoleFields.js';
import { registryKeys, useRegistryLookups } from './registry-ui.js';

type Step = 'identifier' | 'data' | 'roles';

interface Draft {
  country: string;
  typeCode: string;
  vatNumber: string;
  taxCode: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
  province: string;
  email: string;
  pec: string;
  phone: string;
  website: string;
  sdiCode: string;
  notes: string;
  roles: Record<string, Partial<Record<RoleDataField, string>>>;
  classifications: Record<string, string>;
  /** Entered without partita IVA or codice fiscale: the subject stays «da completare». */
  withoutTaxId: boolean;
}

/**
 * New subject (proposal §2.5): the tax identifier first, so duplicates never
 * start; a subject already in the archive but not visible to the user leads to
 * an unlock request, without showing anything about it.
 */
export function NewSubjectPage() {
  usePageTitle('Nuovo soggetto');
  const lookups = useRegistryLookups();
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>('identifier');
  const [draft, setDraft] = useState<Draft>({
    // From a subject of the ledgers (1.47): its identifiers and role come along.
    country: params.get('paese') ?? 'IT',
    // A collaborator from Persone (2.39) is a professional with a VAT number.
    typeCode: params.get('per') === 'collaboratore' ? 'FPF' : 'FPR',
    vatNumber: params.get('piva') ?? '',
    taxCode: params.get('cf') ?? '',
    name: params.get('name') ?? '',
    address: '',
    postalCode: '',
    city: '',
    province: '',
    email: '',
    pec: '',
    phone: '',
    website: '',
    sdiCode: '',
    notes: '',
    roles: { [params.get('ruolo') === 'F' ? 'F' : 'C']: {} },
    classifications: {},
    withoutTaxId: false,
  });
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <section className="stack narrow-page">
      <div className="page-header">
        <div>
          <h1>Nuovo soggetto</h1>
          <p className="muted">Si parte dall’identificativo fiscale: così non nascono doppioni.</p>
        </div>
      </div>
      {params.get('ritorno') && (
        <p className="notice info">
          Stai inserendo <strong>{draft.name || 'un nuovo soggetto'}</strong>{' '}
          {params.get('mastrino')
            ? 'dal mastrino: appena salvato viene abbinato e torni alla pagina di prima.'
            : params.get('per') === 'collaboratore'
              ? 'per un collaboratore: appena salvato torni in Persone con il fornitore già scelto.'
              : 'per un preventivo: appena salvato torni lì con il cliente già scelto.'}
        </p>
      )}
      <ol className="steps">
        {(
          [
            ['identifier', 'Identificativo fiscale'],
            ['data', 'Dati'],
            ['roles', 'Ruoli e classificazioni'],
          ] as const
        ).map(([key, label], index, all) => {
          const current = all.findIndex(([item]) => item === step);
          return (
            <li
              key={key}
              className={index === current ? 'current' : index < current ? 'done' : undefined}
            >
              <b>{index < current ? '✓' : index + 1}</b>
              {label}
            </li>
          );
        })}
      </ol>
      {lookups.data && step === 'identifier' && (
        <IdentifierStep
          draft={draft}
          lookups={lookups.data}
          set={set}
          onFree={(ids) => {
            setDraft((d) => ({ ...d, ...ids, withoutTaxId: false }));
            setStep('data');
          }}
          onWithout={() => {
            setDraft((d) => ({ ...d, vatNumber: '', taxCode: '', withoutTaxId: true }));
            setStep('data');
          }}
        />
      )}
      {lookups.data && step === 'data' && (
        <DataStep
          draft={draft}
          set={set}
          onBack={() => setStep('identifier')}
          onNext={() => setStep('roles')}
        />
      )}
      {lookups.data && step === 'roles' && (
        <RolesStep draft={draft} lookups={lookups.data} set={set} onBack={() => setStep('data')} />
      )}
    </section>
  );
}

/**
 * Created or found from a subject of the ledgers (1.47): matched to it by its
 * key, for every year, as «Abbina» in Tesoreria › Mastrini.
 */
async function linkLedger(key: string | null, subjectId: string): Promise<void> {
  if (!key) return;
  await apiRequest('/treasury/ledgers/links', { method: 'PUT', body: { key, subjectId } });
}

function IdentifierStep({
  draft,
  lookups,
  set,
  onFree,
  onWithout,
}: {
  draft: Draft;
  lookups: RegistryLookups;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
  onFree: (ids: { vatNumber: string; taxCode: string }) => void;
  onWithout: () => void;
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<TaxIdCheckResponse | null>(null);
  const [message, setMessage] = useState('');
  const type = lookups.types.find((item) => item.code === draft.typeCode);
  const body = {
    country: draft.country,
    typeCode: draft.typeCode,
    vatNumber: draft.vatNumber,
    taxCode: draft.taxCode,
  };

  const check = useMutation({
    mutationFn: () =>
      apiRequest<TaxIdCheckResponse>('/subjects/tax-id-check', { method: 'POST', body }),
    onSuccess: (response) => {
      setResult(response);
      // A company of a Gruppo IVA (2.31): first the notice, then on with «Continua».
      if (response.status === 'free' && !response.vatGroup?.length)
        onFree({ vatNumber: response.vatNumber ?? '', taxCode: response.taxCode ?? '' });
    },
  });
  const request = useMutation({
    mutationFn: () =>
      apiRequest<{ status: 'pending' }>('/unlock-requests', {
        method: 'POST',
        body: { ...body, message },
      }),
    onSuccess: () => setResult({ status: 'hidden', request: 'pending' }),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    check.mutate();
  };
  const errors = result?.status === 'invalid' ? result.errors : {};

  return (
    <form className="card stack" onSubmit={submit} noValidate>
      <div className="form-grid">
        <Field label="Paese">
          <select
            value={draft.country}
            onChange={(e) => {
              set('country', e.target.value);
              setResult(null);
            }}
          >
            {COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tipologia" hint={type ? `Obbligatorio: ${type.taxIdRequirement}` : undefined}>
          <select
            value={draft.typeCode}
            onChange={(e) => {
              set('typeCode', e.target.value);
              setResult(null);
            }}
          >
            {lookups.types.map((item) => (
              <option key={item.code} value={item.code}>
                {item.description}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={draft.country === 'IT' ? 'Partita IVA' : 'Partita IVA o identificativo fiscale'}
          error={errors.vatNumber}
        >
          <input
            className="mono-input"
            value={draft.vatNumber}
            autoComplete="off"
            onChange={(e) => {
              set('vatNumber', e.target.value);
              setResult(null);
            }}
          />
        </Field>
        {draft.country === 'IT' && (
          <Field
            label="Codice fiscale"
            error={errors.taxCode}
            hint="Per le aziende è di solito uguale alla partita IVA."
          >
            <input
              className="mono-input"
              value={draft.taxCode}
              autoComplete="off"
              onChange={(e) => {
                set('taxCode', e.target.value.toUpperCase());
                setResult(null);
              }}
            />
          </Field>
        )}
      </div>

      {result?.status === 'free' && result.vatGroup?.length ? (
        <div className="notice warn">
          <p>
            <strong>Partita IVA di un Gruppo IVA.</strong> La usa già{' '}
            {result.vatGroup
              .map((item) => `${item.name}${item.taxCode ? ` (CF ${item.taxCode})` : ''}`)
              .join(', ')}
            . Con un codice fiscale diverso è un’altra società dello stesso gruppo: avrà la sua
            anagrafica, collegata alle altre.
          </p>
          {result.taxCode && result.taxCode !== result.vatNumber ? (
            <button
              type="button"
              className="primary"
              onClick={() =>
                onFree({ vatNumber: result.vatNumber ?? '', taxCode: result.taxCode ?? '' })
              }
            >
              Continua: è una società del gruppo
            </button>
          ) : (
            <p className="small">
              Scrivi il codice fiscale della società: nel Gruppo IVA è quello che la distingue dalle
              altre.
            </p>
          )}
        </div>
      ) : null}

      {result?.status === 'visible' && (
        <div className="notice">
          <strong>Esiste già: {result.name}.</strong> Non si crea un secondo soggetto con lo stesso
          identificativo.
          <div className="row">
            {params.get('mastrino') && (
              <button
                type="button"
                className="small primary"
                onClick={() =>
                  void linkLedger(params.get('mastrino'), result.subjectId).then(async () => {
                    await queryClient.invalidateQueries({ queryKey: ['treasury'] });
                    navigate(params.get('ritorno') ?? `/anagrafica/${result.subjectId}`);
                  })
                }
              >
                Abbina al mastrino
              </button>
            )}
            {params.get('per') === 'collaboratore' && params.get('ritorno') && (
              <button
                type="button"
                className="small primary"
                onClick={() =>
                  navigate(`${params.get('ritorno')}?cliente=${result.subjectId}`, {
                    replace: true,
                  })
                }
              >
                Usa per il collaboratore
              </button>
            )}
            <button
              type="button"
              className="small"
              onClick={() => navigate(`/anagrafica/${result.subjectId}`)}
            >
              Apri
            </button>
            <button
              type="button"
              className="small primary"
              onClick={() => navigate(`/anagrafica/${result.subjectId}?ruolo=nuovo`)}
            >
              Aggiungi un ruolo
            </button>
          </div>
        </div>
      )}
      {result?.status === 'hidden' && (
        <div className="notice stack">
          <strong>Anagrafica presente in archivio, contattare un amministratore.</strong>
          {result.request === 'pending' ? (
            <span>
              Richiesta di sblocco inviata: sei in attesa della decisione dell’amministratore.
            </span>
          ) : (
            <>
              <span>
                {result.request === 'rejected'
                  ? 'La tua richiesta precedente è stata rifiutata. Puoi inviarne un’altra spiegando il motivo.'
                  : 'Non puoi vedere né usare questo soggetto finché un amministratore non lo sblocca per te.'}
              </span>
              <Field label="Messaggio per l’amministratore (facoltativo)">
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Es. il cliente mi ha chiesto un preventivo"
                />
              </Field>
              <div>
                <button
                  type="button"
                  className="small primary"
                  disabled={request.isPending}
                  onClick={() => request.mutate()}
                >
                  Chiedi lo sblocco
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {(check.error ?? request.error) && (
        <p className="error">{(check.error ?? request.error)!.message}</p>
      )}
      <div className="actions">
        <Link to="/anagrafica" className="button-link">
          Annulla
        </Link>
        <button type="button" onClick={onWithout}>
          Non ce l’ho ora
        </button>
        <button type="submit" className="primary" disabled={check.isPending}>
          Controlla e continua
        </button>
      </div>
      <p className="muted small">
        Senza partita IVA né codice fiscale il soggetto nasce «da completare»: si usa nelle bozze,
        ma non si può emettere un preventivo finché manca l’identificativo.
      </p>
    </form>
  );
}

function DataStep({
  draft,
  set,
  onBack,
  onNext,
}: {
  draft: Draft;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const text = (key: keyof Draft, label: string, required = false) => (
    <Field label={required ? `${label} *` : label}>
      <input value={draft[key] as string} onChange={(e) => set(key, e.target.value as never)} />
    </Field>
  );
  return (
    <form
      className="card stack"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!draft.name.trim()) return setError('La ragione sociale è obbligatoria.');
        onNext();
      }}
    >
      <p className="muted small">
        Identificativo: <span className="code">{draft.vatNumber || draft.taxCode}</span>
      </p>
      <div className="form-grid">
        {text('name', 'Ragione sociale / Cognome e nome', true)}
        {text('address', 'Indirizzo')}
        {text('postalCode', 'CAP')}
        {text('city', 'Comune')}
        {text('province', 'Provincia')}
        {text('email', 'E-mail')}
        {text('pec', 'PEC')}
        {text('phone', 'Telefono')}
        {text('website', 'Sito')}
        {text('sdiCode', 'Codice destinatario SDI')}
      </div>
      <Field label="Note">
        <textarea rows={3} value={draft.notes} onChange={(e) => set('notes', e.target.value)} />
      </Field>
      {error && <p className="error">{error}</p>}
      <div className="actions">
        <button type="button" onClick={onBack}>
          Indietro
        </button>
        <button type="submit" className="primary">
          Continua
        </button>
      </div>
    </form>
  );
}

function RolesStep({
  draft,
  lookups,
  set,
  onBack,
}: {
  draft: Draft;
  lookups: RegistryLookups;
  set: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
  onBack: () => void;
}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const canSuppliers = useCan('anagrafica.suppliers');
  const canReserved = useCan('anagrafica.reserved-roles');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const roles = lookups.roles.filter(
    (role) =>
      (!role.reserved || canReserved) &&
      (role.code !== 'F' || canSuppliers) &&
      (role.code !== 'D' || draft.typeCode === 'FPF'),
  );

  const create = useMutation({
    mutationFn: (body: unknown) =>
      apiRequest<SubjectResponse>('/subjects', { method: 'POST', body }),
    onSuccess: async (subject) => {
      await linkLedger(params.get('mastrino'), subject.id);
      await queryClient.invalidateQueries({ queryKey: registryKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      // Came here from another page (a quote): back there with the new subject.
      const back = params.get('ritorno');
      navigate(back ? `${back}?cliente=${subject.id}` : `/anagrafica/${subject.id}`, {
        replace: true,
      });
    },
    onError: (error) => showServerError(error, setErrors, setFormError),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const { roles: selected, classifications, ...fields } = draft;
    const body = {
      ...fields,
      roles: Object.entries(selected).map(([roleCode, data]) => ({ roleCode, ...data })),
      classifications: Object.fromEntries(
        Object.entries(classifications).filter(([, code]) => code),
      ),
    };
    const result = validate(createSubjectSchema, body);
    setErrors(result.errors);
    if (Object.keys(result.errors).length === 0) create.mutate(body);
    else setFormError(Object.values(result.errors)[0] ?? 'Controlla i dati');
  };

  return (
    <form className="card stack" onSubmit={submit} noValidate>
      {draft.withoutTaxId && (
        <p className="notice">
          Senza partita IVA né codice fiscale il soggetto nasce <strong>da completare</strong>: si
          sceglie nelle bozze, ma per emettere un preventivo bisognerà prima completarlo.
        </p>
      )}
      <fieldset className="stack">
        <legend>Ruoli: ognuno riceve il suo codice</legend>
        {roles.map((role) => {
          const selected = draft.roles[role.code];
          return (
            <div key={role.code} className={selected ? 'role-option selected' : 'role-option'}>
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(selected)}
                  onChange={(e) => {
                    const next = { ...draft.roles };
                    if (e.target.checked) next[role.code] = {};
                    else delete next[role.code];
                    set('roles', next);
                  }}
                />
                <strong>{role.description}</strong>
                <span className="muted small">
                  codice {role.prefix}…{role.reserved ? ' · riservato' : ''}
                </span>
              </label>
              {selected && (
                <RoleFields
                  fields={role.requiredData}
                  lookups={lookups}
                  value={selected}
                  errors={errors}
                  onChange={(value) => set('roles', { ...draft.roles, [role.code]: value })}
                />
              )}
            </div>
          );
        })}
      </fieldset>
      {lookups.classifications.length > 0 && (
        <fieldset className="form-grid">
          <legend>Classificazioni</legend>
          {lookups.classifications.map((table) => (
            <Field key={table.key} label={table.name}>
              <select
                value={draft.classifications[table.key] ?? ''}
                onChange={(e) =>
                  set('classifications', { ...draft.classifications, [table.key]: e.target.value })
                }
              >
                <option value="">—</option>
                {table.entries.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.description}
                  </option>
                ))}
              </select>
            </Field>
          ))}
        </fieldset>
      )}
      {formError && <p className="error">{formError}</p>}
      <div className="actions">
        <button type="button" onClick={onBack}>
          Indietro
        </button>
        <button type="submit" className="primary" disabled={create.isPending}>
          {create.isPending ? 'Creazione…' : 'Crea soggetto'}
        </button>
      </div>
    </form>
  );
}
